import { Board } from '../engine/board';
import { DELTA, type Dir, type Level } from '../engine/level';
import { attachInput } from './input';
import { Renderer } from './renderer';
import { fitRoot, watchViewport } from './viewport';

export type PlayEvents = { onCleared(level: Level, moves: number, stars?: number): void; onBack(): void; onNext(level: Level): void; hasNext(level: Level): boolean; best(level: Level): number | undefined };

export class PlayScreen {
  private board: Board;
  private renderer: Renderer;
  private root: HTMLElement;
  private detach: () => void; private unwatch: () => void;
  private raf = 0; private done = false;
  private anim: { r: number; c: number; dr: number; dc: number; t: number; t0: number } | null = null;
  private roll: { path: [number, number][]; t0: number } | null = null;
  private pressed: [number, number] | null = null;

  constructor(host: HTMLElement, private level: Level, private ev: PlayEvents) {
    this.board = new Board(level);
    this.root = document.createElement('div'); this.root.className = 'play';
    this.root.innerHTML = `
      <div class="hud top"><button class="icon back" aria-label="목록">‹</button><div class="title">level ${level.n} <span class="dim">${level.rows}×${level.cols}</span></div><button class="icon retry" aria-label="다시">↻</button></div>
      <canvas class="board"></canvas>
      <div class="hud bottom"><span class="moves"></span><span class="target"></span><span class="best"></span></div>
      <div class="overlay" hidden><div class="card"><div class="big">CLEAR</div><div class="sub"></div><div class="row"><button class="btn again">다시</button><button class="btn next">다음</button></div></div></div>`;
    host.replaceChildren(this.root);
    const canvas = this.root.querySelector<HTMLCanvasElement>('canvas')!;
    this.renderer = new Renderer(canvas);
    this.root.querySelector('.back')!.addEventListener('click', () => ev.onBack());
    this.root.querySelector('.retry')!.addEventListener('click', () => this.restart());
    this.root.querySelector('.again')!.addEventListener('click', () => this.restart());
    const next = this.root.querySelector<HTMLButtonElement>('.next')!;
    next.hidden = !ev.hasNext(level); next.addEventListener('click', () => ev.onNext(level));
    this.detach = attachInput(canvas, (x, y) => this.renderer.cellAt(x, y, this.board.rows, this.board.cols), {
      onStart: (c) => { this.pressed = c; },
      onMove: (cell) => {
        // 빈칸 쪽으로 끌면 그 방향으로 민다
        if (!this.pressed || this.anim || this.roll) return;
        const [r0, c0] = this.pressed; const [r, c] = cell;
        if (r === r0 && c === c0) return;
        const d: Dir | null = r === r0 ? (c > c0 ? 'e' : 'w') : c === c0 ? (r > r0 ? 's' : 'n') : null;
        if (d && this.board.movableDirs(r0, c0).includes(d)) { this.move(r0, c0, d); }
        this.pressed = null;
      },
      onEnd: () => {
        if (this.pressed && !this.anim && !this.roll) { const [r, c] = this.pressed; const ds = this.board.movableDirs(r, c); if (ds.length === 1) this.move(r, c, ds[0]!); }
        this.pressed = null;
      },
    });
    this.unwatch = watchViewport(() => this.fit());
    const loop = (t: number) => { this.raf = requestAnimationFrame(loop); this.tick(t); this.render(); };
    this.raf = requestAnimationFrame(loop);
  }
  private move(r: number, c: number, d: Dir) {
    const [dr, dc] = DELTA[d];
    if (!this.board.slide(r, c, d)) return;
    // 이미 옮겨졌으니 새 자리에서 반대 방향으로 들어오는 애니메이션
    this.anim = { r: r + dr, c: c + dc, dr: -dr, dc: -dc, t: 1, t0: performance.now() };
  }
  private tick(now: number) {
    if (this.anim) {
      const k = Math.min(1, (now - this.anim.t0) / 120);
      this.anim.t = 1 - k;
      if (k >= 1) {
        this.anim = null;
        const p = this.board.path();
        if (p && !this.done) this.roll = { path: p, t0: now };
      }
    }
    if (this.roll) {
      const per = 90, k = (now - this.roll.t0) / per;
      if (k >= this.roll.path.length - 1) { this.roll = null; this.finish(); }
    }
  }
  private ballPos(now: number): [number, number] | null {
    if (!this.roll) return null;
    const k = Math.min(this.roll.path.length - 1, (now - this.roll.t0) / 90);
    const i = Math.floor(k), f = k - i;
    const a = this.roll.path[i]!, b = this.roll.path[Math.min(i + 1, this.roll.path.length - 1)]!;
    return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f];
  }
  private fit() {
    const [w, h] = fitRoot(this.root);
    this.renderer.canvas.style.top = '44px';
    this.renderer.resize(w, Math.max(100, h - 44 - 36), window.devicePixelRatio || 1, this.board.rows, this.board.cols);
  }
  private render() {
    this.renderer.draw(this.board, this.anim, this.ballPos(performance.now()));
    this.root.querySelector('.moves')!.textContent = `moves ${this.board.moves}`;
    this.root.querySelector('.target')!.textContent = this.level.target !== undefined ? `target ${this.level.target}` : '';
    const best = this.ev.best(this.level);
    this.root.querySelector('.best')!.textContent = best !== undefined ? `best ${best}` : '';
  }
  private finish() {
    if (this.done) return;
    this.done = true;
    const stars = this.board.stars(this.level.target);
    this.root.querySelector('.sub')!.textContent = `${this.board.moves} moves${stars !== undefined ? ' · ' + '★'.repeat(stars) + '☆'.repeat(3 - stars) : ''}`;
    this.root.querySelector<HTMLElement>('.overlay')!.hidden = false;
    this.ev.onCleared(this.level, this.board.moves, stars);
  }
  restart() { this.board = new Board(this.level); this.done = false; this.anim = null; this.roll = null; this.root.querySelector<HTMLElement>('.overlay')!.hidden = true; }
  destroy() { cancelAnimationFrame(this.raf); this.detach(); this.unwatch(); this.root.remove(); }
}
